import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PublicLayout from '../components/PublicLayout';
import SEO from '../components/SEO';

const TOOLS = [
  { title: 'My Plans', desc: 'Create and manage Operational WMPs, D&CWMPs, and Construction Management Plans with council-specific calculations.', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { title: 'Waste Room Builder', desc: 'Model waste storage rooms with real-world equipment dimensions, bin configurations, and clearance checks.', icon: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z' },
  { title: 'Vehicle Access Planner', desc: 'Preliminary swept path assessment for waste collection vehicles — SRV, MRV, and HRV turning radius analysis.', icon: 'M1 3h15v13H1V3zM16 8h4l3 4v5h-7V8z' },
];

export default function Generator() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) navigate('/dashboard');
    else setError(result.error);
  };

  return (
    <PublicLayout>
      <SEO title="Generator — WMP Planning Tools" description="Access PlanGen Consulting's waste management planning tools." canonicalPath="/generator" noIndex />

      <section className="py-16 lg:py-20 bg-[#F8FAFC]" data-testid="generator-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.4fr_0.6fr] gap-12 items-start">
            {/* Left — Tools overview */}
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">PLANGEN GENERATOR</div>
              <h1 className="text-3xl lg:text-4xl font-black tracking-tight text-[#0A2540] leading-tight mb-4" data-testid="generator-headline">
                Waste Management Planning Tools
              </h1>
              <p className="text-base text-slate-600 leading-relaxed mb-8 max-w-xl">
                Our Generator tools help you create council-compliant Waste Management Plans, model waste storage rooms, and assess vehicle access — all in one place.
              </p>

              <div className="space-y-4">
                {TOOLS.map((tool, i) => (
                  <div key={i} className="bg-white rounded-xl border border-slate-100 p-5 shadow-[0_2px_12px_rgb(0,0,0,0.03)]" data-testid={`generator-tool-${i}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={tool.icon} />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-[#0A2540] mb-1">{tool.title}</h3>
                        <p className="text-xs text-slate-500 leading-relaxed">{tool.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Login or Dashboard link */}
            <div className="w-full max-w-sm mx-auto lg:mx-0 lg:ml-auto lg:mt-8">
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-[0_4px_30px_rgb(0,0,0,0.06)] border border-white/60 p-6" data-testid="generator-login-card">
                {isAuthenticated ? (
                  <div className="text-center py-4">
                    <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
                      <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <h2 className="text-lg font-bold text-[#0A2540] mb-1">You&apos;re signed in</h2>
                    <p className="text-slate-400 text-xs mb-5">Access your projects and tools</p>
                    <Link to="/dashboard" className="inline-flex w-full justify-center py-2.5 rounded-full bg-[#0A2540] text-white text-sm font-semibold hover:bg-black transition-colors shadow-lg shadow-slate-900/10" data-testid="generator-go-dashboard">
                      Go to Dashboard
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="text-center mb-4">
                      <h2 className="text-lg font-bold text-[#0A2540]">Sign In</h2>
                      <p className="text-slate-400 text-xs mt-0.5">Access your projects and tools</p>
                    </div>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg text-xs mb-3">{error}</div>
                )}
                <form onSubmit={handleLogin} className="space-y-3">
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                    className="w-full px-4 py-2.5 text-sm bg-slate-50/80 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all placeholder:text-slate-300"
                    placeholder="Email" data-testid="generator-login-email" />
                  <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 text-sm bg-slate-50/80 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all placeholder:text-slate-300"
                    placeholder="Password" data-testid="generator-login-password" />
                  <button type="submit" disabled={loading}
                    className="w-full py-2.5 rounded-full bg-[#0A2540] text-white text-sm font-semibold hover:bg-black transition-colors disabled:opacity-50 shadow-lg shadow-slate-900/10"
                    data-testid="generator-login-submit">
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>
                <div className="text-center mt-4 space-y-1.5">
                  <Link to="/forgot-password" className="block text-xs text-blue-600 hover:text-blue-700">Forgot password?</Link>
                  <p className="text-xs text-slate-400">
                    New here? <Link to="/register" className="text-blue-600 font-medium hover:text-blue-700">Create an account</Link>
                  </p>
                </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
